<template>
    <div v-if="storeLocation" @click="select()" class="ion-no-padding location-info" :class="{ clickable: clickable, 'pa-0': detail }" :data-cy="'location-' + storeLocation.slug">
        <storeStatus v-if="storeLocation.hours" :storeLocation="storeLocation" class="pl-16" />

        <ion-row class="ion-justify-contents-between">
            <ion-col v-if="storeLocation?.name" data-cy="location-name" class="pl-16">
                <h1 class="ion-no-margin" v-if="detail">{{ storeLocation.name }}</h1>
                <h5 class="ion-no-margin" v-else>{{ storeLocation.name }}{{ isRedRobinStore ? ' | Red Robin' : '' }}</h5>
            </ion-col>
            <ion-col v-if="storeLocation?.distance && detail" size="2" class="pl-0">
                {{ storeLocation.distance ? storeLocation.distance + 'mi' : '' }}
            </ion-col>
        </ion-row>

        <ion-row>
            <ion-col class="px-16">
                <addressDisplay :address="storeLocation.address" :inline="!detail" data-cy="location-address" />
                <a v-if="detail && phoneLink" :href="phoneLink.href" class="small bold" data-cy="location-phone">{{ phoneLink.text }}</a>
            </ion-col>
        </ion-row>

        <ion-row class="ion-text-start ion-align-items-center simple-row ion-justify-content-between color-dark" v-if="!detail">
            <ion-col class="small pl-16" v-if="storeLocation?.distance || storeLocation?.distance === 0">
                <span> {{ storeLocation.distance ? storeLocation.distance + 'mi' : '0.00' }}</span>
                <span v-if="storeLocation.hours && storeLocation.hours.length > 0"> | </span>
                <storeTime v-if="storeLocation && storeLocation.hours && storeLocation.hours.length > 0" :storeLocation="storeLocation" showTodayHours boldTime class="d-inline color-dark" />
            </ion-col>
        </ion-row>

        <ion-row v-else class="ion-margin-vertical">
            <ion-item-divider />
            <storeTime v-if="storeLocation" :storeLocation="storeLocation" boldTime />
            <ion-skeleton-text v-else style="width: 50%; margin: 20px 0" />
            <ion-item-divider />
        </ion-row>

        <ion-row v-if="!detail && storeLocation.orderType?.includes('Window')">
            <ion-badge class="mx-16 mt-8 color-light" color="dark" mode="ios">Pick-Up Window</ion-badge>
        </ion-row>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { CardLink } from '@/models/props/cardLink';
import { formatPhoneNumber } from '@/services/stringService';
import { IonRow, IonCol, IonItemDivider, IonSkeletonText, IonBadge } from '@ionic/vue';
import addressDisplay from '@/components/addressDisplay.vue';
import storeTime from '@/components/storeTime.vue';
import storeStatus from '@/components/storeStatus.vue';
import { useRouter, useRoute } from 'vue-router';
import { LocationStatus, StoreLocation } from '@/models/locationModels';

export default defineComponent({
    props: {
        storeLocation: {
            type: Object as PropType<StoreLocation>,
            required: true,
        },
        locationStatus: {
            type: Object as PropType<LocationStatus>,
        },
        clickable: Boolean,
        detail: Boolean,
    },
    components: { IonRow, IonCol, addressDisplay, storeTime, IonItemDivider, IonSkeletonText, IonBadge, storeStatus },
    setup(props) {
        const router = useRouter();
        const route = useRoute();

        const phoneLink = computed(() => {
            if (!props.storeLocation?.phone) return;
            return new CardLink({ text: formatPhoneNumber(props.storeLocation?.phone), href: `tel:${props.storeLocation?.phone}` });
        });

        const isRedRobinStore = computed(() => {
            return props.storeLocation.storeStatus.isRedRobinStore;
        });

        const select = () => {
            if (props.clickable && props.storeLocation) {
                const toPath = { path: `/location/${props.storeLocation?.slug}` };
                if (route.query?.redirect) {
                    toPath['query'] = { redirect: route.query?.redirect };
                }
                router.push(toPath);
            }
        };
        return { phoneLink, select, isRedRobinStore };
    },
});
</script>

<style scoped>
.clickable:hover {
    cursor: pointer;
}
.location-info {
    padding: 15px 10px;
}
h1 {
    font-size: 18px;
    font-weight: bold;
    color: black;
    margin-bottom: 8px;
}
ion-col {
    padding-top: 0;
    padding-bottom: 0;
}
</style>
